import React, { useState } from 'react';
import { sessionsApi } from '../../api/sessions';
import { getTemplateIcon } from '../../utils/templateIcons';
import './Sessions.css';

// Template icon component - shows template type at a glance
const TemplateIcon = ({ template }) => {
  const icon = getTemplateIcon(template);
  return (
    <span
      className="session-card__template-icon"
      title={template ? `${template} session` : 'Open session'}
    >
      {icon}
    </span>
  );
};

const SessionCard = ({ session, onClick, isActive, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(session.name || '');
  const [isSaving, setIsSaving] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setEditName(session.name || '');
    setIsEditing(true);
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditName(session.name || '');
  };

  const handleSaveEdit = async (e) => {
    e.stopPropagation();
    if (!editName.trim() || editName === session.name) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await sessionsApi.update(session.id, { name: editName.trim() });
      if (onRename) {
        onRename(session.id, editName.trim());
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to rename session:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSaveEdit(e);
    } else if (e.key === 'Escape') {
      handleCancelEdit(e);
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      className={`session-card ${isActive ? 'session-card--active' : ''}`}
      onClick={onClick}
    >
      <div className="session-card__header">
        {isEditing ? (
          <div className="session-card__edit-container" onClick={handleInputClick}>
            <input
              type="text"
              className="session-card__edit-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              disabled={isSaving}
            />
            <button
              className="session-card__edit-btn session-card__edit-btn--save"
              onClick={handleSaveEdit}
              disabled={isSaving}
              title="Save"
            >
              ✓
            </button>
            <button
              className="session-card__edit-btn session-card__edit-btn--cancel"
              onClick={handleCancelEdit}
              disabled={isSaving}
              title="Cancel"
            >
              ✕
            </button>
          </div>
        ) : (
          <>
            <TemplateIcon template={session.template} />
            <h3 className="session-card__name">{session.name || 'Unnamed Session'}</h3>
            <button
              className="session-card__rename-btn hover-action"
              onClick={handleEditClick}
              title="Rename session"
            >
              ✏️
            </button>
          </>
        )}
        {session.summary && !isEditing && (
          <span className="session-card__summary-indicator" title="Has summary">✓</span>
        )}
      </div>
      <div className="session-card__metadata">
        <span className="session-card__message-count">
          {session.message_count || 0} {session.message_count === 1 ? 'message' : 'messages'}
        </span>
        <span className="session-card__date">{formatDate(session.created_at)}</span>
      </div>
    </div>
  );
};

export default SessionCard;
